/**
 * Certificate of electronic signature.
 *
 * FLHSMV bulletin 20-019 lets a dealer e-sign the title paperwork, and asks
 * for two things alongside it: a signature certification page carrying the
 * audit trail — timestamps, IP addresses, activity history — and an
 * attestation on company letterhead that the process met NIST assurance
 * level 2 as required by 49 CFR 580.
 *
 * This page is both. It is generated from the event log rather than from
 * anything typed by hand, so it cannot say something the database does not.
 *
 * It states the identity method plainly, including when that method was the
 * weaker one. A certificate that claims in-person licence verification for a
 * signature collected over a link would be worse than no certificate: it
 * would be a false statement on a document going to the state.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import { usDate } from "./mappings";
import type { DealerSettings } from "./mappings";
import { shortHash } from "../lib/signing/stamp";
import { ROLE_LABEL, type SignerRole } from "../lib/signing/anchors";

const INK = rgb(0.07, 0.09, 0.13);
const SOFT = rgb(0.29, 0.34, 0.39);
const RULE = rgb(0.78, 0.80, 0.84);
const NAVY = rgb(0.11, 0.31, 0.85);
const BAND = rgb(0.93, 0.95, 0.98);

const L = 58, R = 554, W = R - L;

export interface CertEvent {
  at: string;
  kind: string;
  actor: string | null;
  ip: string | null;
  detail?: Record<string, unknown> | null;
}

export interface CertSigner {
  role: SignerRole;
  name: string;
  signedAt: string;
  ip: string | null;
  userAgent: string | null;
}

export interface CertificateInput {
  dealer: DealerSettings;
  dealNo: string;
  vehicle: string;
  vin: string;
  sessionId: string;
  mode: "in_person" | "remote";
  /** 'in_person_dl' | 'sms_code' */
  idMethod: string;
  idEvidence: Record<string, unknown> | null;
  /** Staff member who attested to checking the physical licence. */
  attestedBy: string | null;
  consentAt: string | null;
  consentSha256: string | null;
  signers: CertSigner[];
  events: CertEvent[];
  documents: { title: string; pages: number }[];
  /** Hash of the signed packet, without this page. */
  packetSha256: string;
}

/** Plain English for the event log, so the page reads rather than decodes. */
const EVENT_LABEL: Record<string, string> = {
  session_created: "Signing session created",
  consent_shown: "Electronic records disclosure shown",
  consent_given: "Consent to do business electronically given",
  identity_attested: "Identity verified against physical photo ID by dealer staff",
  code_sent: "One-time access code sent",
  code_verified: "One-time access code entered correctly",
  code_failed: "Incorrect access code entered",
  link_opened: "Signing link opened",
  document_viewed: "Document presented to signer",
  signature_adopted: "Signature drawn and adopted",
  signature_applied: "Signature applied to the packet",
  session_completed: "Signing completed and packet sealed",
  packet_emailed: "Signed packet emailed to the customer",
  declined: "Signer declined to sign",
};

const label = (kind: string) => EVENT_LABEL[kind] ?? kind.replace(/_/g, " ");

function wrap(text: string, font: PDFFont, size: number, width: number): string[] {
  const out: string[] = [];
  for (const para of text.split("\n")) {
    let line = "";
    for (const word of para.split(/\s+/)) {
      const trial = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(trial, size) <= width) line = trial;
      else { if (line) out.push(line); line = word; }
    }
    out.push(line);
  }
  return out;
}

const stamp = (iso: string) => {
  const d = new Date(iso);
  const date = usDate(iso.slice(0, 10));
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  });
  return `${date}  ${time}`;
};

/** How the signer was identified, in the words the certificate should use. */
function identityWording(input: CertificateInput): { headline: string; detail: string } {
  if (input.idMethod === "in_person_dl") {
    const ev = input.idEvidence ?? {};
    const bits = [
      ev.id_type ? String(ev.id_type) : "photo identification",
      ev.id_state ? `issued by ${ev.id_state}` : null,
      ev.id_last4 ? `ending ${ev.id_last4}` : null,
      ev.id_expiration ? `expiring ${usDate(String(ev.id_expiration))}` : null,
    ].filter(Boolean).join(", ");
    return {
      headline: "In-person verification of government-issued photo identification",
      detail:
        `The signer appeared in person at the dealership. A representative of `
        + `${input.dealer.legalName} examined the signer's ${bits} and confirmed that `
        + `the photograph and name matched the person signing. `
        + (input.attestedBy ? `Verified by ${input.attestedBy}. ` : "")
        + `In-person inspection of a government-issued photo ID is the identity `
        + `proofing method described at NIST SP 800-63A for identity assurance `
        + `level 2.`,
    };
  }
  return {
    headline: "Remote verification by one-time code to a known telephone number",
    detail:
      "The signer was sent a single-use code to the telephone number already on "
      + "file for them from an earlier, in-person transaction, and entered that code "
      + "before signing. This establishes control of a known channel. It is a weaker "
      + "form of identity proofing than in-person inspection of photo identification, "
      + "and is recorded here as what it is.",
  };
}

export async function buildSignatureCertificate(
  input: CertificateInput,
): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const reg = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const mono = await doc.embedFont(StandardFonts.Courier);

  let page: PDFPage = doc.addPage([612, 792]);
  const pages: PDFPage[] = [page];
  let y = 742;

  const at = (t: string, x: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x, y, size, font: f, color });
  const right = (t: string, size = 9, f = reg, color = SOFT) =>
    page.drawText(t, { x: R - f.widthOfTextAtSize(t, size), y, size, font: f, color });
  const rule = (yy: number, color = RULE, thickness = 0.75) =>
    page.drawLine({ start: { x: L, y: yy }, end: { x: R, y: yy }, thickness, color });

  const room = (needed: number) => {
    if (y - needed >= 74) return;
    page = doc.addPage([612, 792]);
    pages.push(page);
    y = 742;
    at("Certificate of Electronic Signature — continued", L, 9, reg, SOFT);
    y -= 10;
    rule(y);
    y -= 22;
  };

  const para = (text: string, size = 9.5, leading = 13, gap = 10) => {
    const lines = wrap(text, reg, size, W);
    room(lines.length * leading + gap);
    for (const line of lines) { at(line, L, size); y -= leading; }
    y -= gap;
  };

  const heading = (text: string) => {
    y -= 8;
    room(34);
    at(text.toUpperCase(), L, 8.5, bold, NAVY);
    y -= 6;
    rule(y);
    y -= 16;
  };

  const pair = (k: string, v: string) => {
    room(15);
    at(k, L, 8.5, reg, SOFT);
    const lines = wrap(v, reg, 9.5, W - 150);
    at(lines[0] ?? "", L + 150, 9.5);
    y -= 13;
    for (const extra of lines.slice(1)) { at(extra, L + 150, 9.5); y -= 12; }
    y -= 2;
  };

  // ---- letterhead. The attestation has to be ON company letterhead. -------
  at((input.dealer.dba ?? input.dealer.legalName).toUpperCase(), L, 15, bold);
  let ry = y + 3;
  for (const line of [
    input.dealer.street,
    `${input.dealer.city}, ${input.dealer.state} ${input.dealer.zip}`,
    `FL Dealer License ${input.dealer.flhsmvLicenseNo}`,
  ]) {
    page.drawText(line, {
      x: R - reg.widthOfTextAtSize(line, 8.5), y: ry, size: 8.5, font: reg, color: SOFT,
    });
    ry -= 10.5;
  }
  y -= 24;
  rule(y, NAVY, 2);
  y -= 24;

  at("CERTIFICATE OF ELECTRONIC SIGNATURE", L, 17, bold);
  y -= 15;
  at("Signature certification and audit trail", L, 9.5, reg, SOFT);
  right(`Deal ${input.dealNo}`, 9.5, reg, SOFT);
  y -= 24;

  // ---- what was signed ---------------------------------------------------
  heading("What was signed");
  pair("Deal", input.dealNo);
  pair("Vehicle", input.vehicle);
  pair("VIN", input.vin);
  pair("Documents", input.documents.map((d) => `${d.title} (${d.pages}pp)`).join("; "));
  pair("Session", input.sessionId);
  y -= 6;

  // ---- who signed --------------------------------------------------------
  heading("Who signed");
  for (const s of input.signers) {
    room(40);
    page.drawRectangle({ x: L, y: y - 30, width: W, height: 40, color: BAND });
    at(s.name, L + 8, 10.5, bold);
    right(ROLE_LABEL[s.role], 8.5, bold, NAVY);
    y -= 14;
    at(`Signed ${stamp(s.signedAt)}`, L + 8, 8.5, reg, SOFT);
    y -= 11;
    at(`IP ${s.ip ?? "not recorded"}`, L + 8, 8, mono, SOFT);
    y -= 25;
  }

  // ---- identity ----------------------------------------------------------
  const identity = identityWording(input);
  heading("How the signer was identified");
  at(identity.headline, L, 10, bold);
  y -= 15;
  para(identity.detail);

  // ---- consent -----------------------------------------------------------
  heading("Consent to electronic records");
  if (input.consentAt) {
    para(
      `Before any document was presented, the signer was shown a disclosure `
      + `explaining that the paperwork would be signed and delivered electronically, `
      + `that a paper copy could be requested at no charge, and how to withdraw `
      + `consent. The signer agreed on ${stamp(input.consentAt)}. The exact wording `
      + `shown is fixed by its hash below, so the disclosure that was agreed to can `
      + `be identified later.`,
    );
    if (input.consentSha256) {
      pair("Disclosure hash", shortHash(input.consentSha256));
    }
  } else {
    para("No consent record exists for this session.");
  }

  // ---- audit trail -------------------------------------------------------
  const WHO_X = L + 322, IP_X = L + 424;
  const tableHead = () => {
    at("WHEN", L, 7.5, bold, SOFT);
    at("WHAT", L + 118, 7.5, bold, SOFT);
    at("WHO", WHO_X, 7.5, bold, SOFT);
    at("IP", IP_X, 7.5, bold, SOFT);
    y -= 5;
    rule(y);
    y -= 12;
  };

  heading("Audit trail");
  room(16);
  tableHead();

  for (const e of input.events) {
    const what = wrap(label(e.kind), reg, 8.5, 198);
    const before = pages.length;
    room(what.length * 10 + 6);
    // A run of timestamps continuing onto a fresh page with no column
    // headings is a table nobody can read. Repeat them after a break.
    if (pages.length > before) tableHead();

    at(stamp(e.at), L, 8, mono);
    at(what[0] ?? "", L + 118, 8.5);

    /* Names are truncated to what the column holds, with an ellipsis, rather
       than clipped mid-word: "Cape Auto Solutions LL" reads like a different
       company. */
    const who = e.actor ?? "—";
    const fitted = who.length > 24 ? `${who.slice(0, 23)}…` : who;
    at(fitted, WHO_X, 8.5, reg, SOFT);
    at(e.ip ?? "—", IP_X, 8, mono, SOFT);
    y -= 10;
    for (const extra of what.slice(1)) { at(extra, L + 118, 8.5); y -= 10; }
    y -= 2;
  }

  y -= 10;

  // ---- integrity ---------------------------------------------------------
  heading("Document integrity");
  para(
    "The signed packet was hashed with SHA-256 at the moment it was sealed. A copy "
    + "produced later can be hashed again and compared against the value below; if "
    + "they match, it is the document that was signed, unaltered.",
  );
  room(20);
  at("SHA-256", L, 8.5, reg, SOFT);
  at(shortHash(input.packetSha256), L + 150, 9, mono);
  y -= 13;
  at(input.packetSha256, L + 150, 6.5, mono, SOFT);
  y -= 22;

  // ---- the attestation FLHSMV asks for -----------------------------------
  heading("Attestation");
  para(
    `${input.dealer.legalName}, Florida dealer license ${input.dealer.flhsmvLicenseNo}, `
    + `attests that the electronic signatures recorded in this certificate were `
    + `obtained through a process using the identity proofing and authentication `
    + `method described above, that the audit trail set out here is a complete and `
    + `unaltered record generated by the system at the time of signing, and that `
    + `these records are retained for the periods required by law.`,
    9.5, 13, 8,
  );
  para(
    "This attestation is made with reference to National Highway Traffic Safety "
    + "Administration rule 49 CFR Part 580 and FLHSMV information bulletin 20-019, "
    + "concerning electronic signatures on odometer disclosures and title "
    + "applications at identity assurance level 2.",
    9, 12.5, 16,
  );

  room(70);
  rule(y);
  y -= 16;
  at("Signed for the dealership", L, 8.5, reg, SOFT);
  y -= 30;
  page.drawLine({ start: { x: L, y }, end: { x: L + 220, y }, thickness: 0.8, color: INK });
  page.drawLine({ start: { x: L + 250, y }, end: { x: L + 390, y }, thickness: 0.8, color: INK });
  at("Authorized representative", L, 7.5, reg, SOFT);
  at("Date", L + 250, 7.5, reg, SOFT);

  // ---- footers -----------------------------------------------------------
  pages.forEach((p, i) => {
    p.drawLine({ start: { x: L, y: 62 }, end: { x: R, y: 62 }, thickness: 0.75, color: RULE });
    p.drawText(`${input.dealer.legalName}  ·  Certificate of Electronic Signature`,
      { x: L, y: 48, size: 7.5, font: reg, color: SOFT });
    const tail = `Deal ${input.dealNo}  ·  Page ${i + 1} of ${pages.length}`;
    p.drawText(tail,
      { x: R - reg.widthOfTextAtSize(tail, 7.5), y: 48, size: 7.5, font: reg, color: SOFT });
  });

  doc.setTitle(`${input.dealNo} certificate of electronic signature`);
  doc.setProducer("FrontLot");
  return doc.save();
}

/**
 * The disclosure shown before signing.
 *
 * ESIGN section 101(c) requires the consumer be told, before consenting, that
 * they may have the record on paper, how to withdraw consent, and what
 * hardware and software they need to read it. Kept as one exported constant
 * because its hash is stored with the consent: if this text changes, old
 * consents still identify the wording their signer actually saw.
 */
export const ESIGN_DISCLOSURE = `Consent to use electronic records and signatures

Before you sign, please read this.

We would like to give you the paperwork for this vehicle purchase electronically,
and to have you sign it electronically, rather than on paper. Florida and federal
law both allow this, and an electronic signature has the same legal effect as one
made with a pen.

You do not have to agree. If you would rather sign on paper, tell us and we will
print everything. It will not change the price, the vehicle, or anything else
about your purchase.

A paper copy. You may ask us for a paper copy of any document you sign, at any
time, at no charge. We will also email you a complete copy of everything you sign
today.

Withdrawing consent. You may withdraw your consent at any time before you finish
signing, and we will print the remaining documents instead. Withdrawing consent
does not undo documents you have already signed.

What you need. To read these documents you need a device with a modern web
browser and the ability to open PDF files, and an email address where we can send
your copies. If you can read this, your device is sufficient.

Keeping your copy. We keep a copy of everything you sign for at least ten years,
and we can produce it for you on request.

By continuing, you agree to use electronic records and electronic signatures for
this purchase, and you confirm you can access documents in the way described
above.`;
